import type { AfterViewChecked, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { FeatureFlag } from 'app/common/enums/feature-flag.enum';
import { Tooltip } from 'bootstrap';
import { StringConstants } from 'app/rcr-exchange/constants/string-constants';
import { ErrorHandlerService } from 'app/shared/services/error-handler.service';
import { FeeDisclaimerService } from 'app/shared/services/fee-disclaimer.service';
import moment from 'moment-timezone';
import type { PaginationInstance } from 'ngx-pagination';
import { finalize } from 'rxjs';
import { MegasService } from '../services/megas.service';

@Component({
  standalone: false,
  selector: 'invd-committed-mega-table',
  templateUrl: './committed-mega-table.component.html',
  styleUrls: ['./committed-mega-table.component.scss'],
})
export class CommittedMegaTableComponent implements OnInit, AfterViewChecked {
  private tooltipsInitialized = false;

  @Input()
  public MFFixedMegaList: any;
  @Input()
  public MFARMMegaList: any;
  @Input()
  public SFARMMegaList: any;
  @Input()
  public SFFixedMegaList: any;
  @Input()
  public showMFFixedMega: boolean;
  @Input()
  public showMFARMMega: boolean;
  @Input()
  public showSFFixedMega: boolean;
  @Input()
  public showSFARMMega: boolean;
  @Input() singleFamilyARMMega: string;
  @Input() multiFamilyFixedMega: string;
  @Input() singleFamilyFixedMega: string;
  @Input() multiFamilyARMMega: string;

  @Input()
  public showMessage: boolean;
  @Input() displayPagi: boolean;

  @Output()
  editMegaClick = new EventEmitter<any>();

  @Output() downloadCollateralClicked = new EventEmitter<any>();
  @Output() deallocation: EventEmitter<any> = new EventEmitter();

  @Output()
  fileUploaded = new EventEmitter<any>();
  @Input() totalCommittedVolumeSFFIXMEGA: number;
  @Input() totalCommittedVolumeSFARMMEGA: number;
  @Input() totalCommittedVolumeMFFIXMEGA: number;
  @Input() totalCommittedVolumeMFARMMEGA: number;
  @Input() totalCommittedCurrentMonthSFFIXMEGA: number;
  @Input() totalCommittedCurrentMonthSFARMMEGA: number;
  @Input() totalCommittedCurrentMonthMFFIXMEGA: number;
  @Input() totalCommittedCurrentMonthMFARMMEGA: number;

  public presentSortedColumn = '';
  public presentSortedColumnIsAscending = false;
  @Input() hasCreateOrEditMegaAccessFlag: boolean;
  @Input() hasListSettledAndCommittedAccessFlag: boolean;
  @Input() hasCollateralUploadAccessFlag: boolean;
  @Input() isInternalUser: boolean;
  @Input() featuretoggle: string[];

  public showDeallocateModal = false;
  public deallocationError = false;
  public processingDeallocation = false;
  public LDshowUpload = false;
  public LDUploadPoolInfoFlag = false;
  public LDDeallocateButton = false;
  public editMegaClickedCount = 0;
  public currentTrade: any;
  public disabledDeallocateText = '';
  public disclaimerMessage = '';

  public config: PaginationInstance = {
    id: 'committed-mega-pagination',
    itemsPerPage: 50,
    currentPage: 1,
  };

  private router = inject(Router);
  private feeDisclaimerService = inject(FeeDisclaimerService);
  private megaService = inject(MegasService);
  private errorHandlerService = inject(ErrorHandlerService);
  private readonly cdr = inject(ChangeDetectorRef);

  onSelectCommittedMega(committedMega: any) {
    this.router.navigate([
      '/mega-trade/update-details',
      committedMega.poolNumber,
      committedMega.cusip,
    ]);
  }

  ngOnInit() {
    this.editMegaClickedCount = 0;
    this.onInitTooltip();

    this.LDshowUpload = this.featuretoggle && this.featuretoggle.includes(FeatureFlag.COLAT_UPLOAD);
    this.LDUploadPoolInfoFlag =
      this.featuretoggle && this.featuretoggle.includes(FeatureFlag.COLAT_UPD_MENU);
    this.LDDeallocateButton =
      this.featuretoggle && this.featuretoggle.includes(FeatureFlag.DEALLOCATE_MEGA);

    this.disabledDeallocateText = StringConstants.DISABLED_DEALLOCATE_TEXT;
  }

  getSum(itemsList: any): number {
    let sum = 0;
    for (const item of itemsList) {
      sum += item.tradeAmount;
    }
    return sum;
  }

  protected sortByGridByColumnName(columnName): void {
    if (this.presentSortedColumn !== columnName) {
      this.presentSortedColumnIsAscending = true;
    } else {
      this.presentSortedColumnIsAscending = !this.presentSortedColumnIsAscending;
    }
    const isAscending = this.presentSortedColumnIsAscending ? 1 : -1;

    if (this.showSFFixedMega) {
      this.SFFixedMegaList.sort((a: any, b: any) => {
        const textA = a[columnName];
        const textB = b[columnName];
        if (typeof textA === 'string' && typeof textB === 'string') {
          return textA.toString().toUpperCase() < textB.toString().toUpperCase()
            ? -1 * isAscending
            : textA.toString().toUpperCase() > textB.toString().toUpperCase()
              ? 1 * isAscending
              : 0;
        } else if (typeof textA === 'number' && typeof textB === 'number') {
          return textA < textB ? -1 * isAscending : textA > textB ? 1 * isAscending : 0;
        }
      });
    } else if (this.showSFARMMega) {
      this.SFARMMegaList.sort((a: any, b: any) => {
        const textA = a[columnName];
        const textB = b[columnName];
        if (typeof textA === 'string' && typeof textB === 'string') {
          return textA.toString().toUpperCase() < textB.toString().toUpperCase()
            ? -1 * isAscending
            : textA.toString().toUpperCase() > textB.toString().toUpperCase()
              ? 1 * isAscending
              : 0;
        } else if (typeof textA === 'number' && typeof textB === 'number') {
          return textA < textB ? -1 * isAscending : textA > textB ? 1 * isAscending : 0;
        }
      });
    } else if (this.showMFFixedMega) {
      this.MFFixedMegaList.sort((a: any, b: any) => {
        const textA = a[columnName];
        const textB = b[columnName];
        if (typeof textA === 'string' && typeof textB === 'string') {
          return textA.toString().toUpperCase() < textB.toString().toUpperCase()
            ? -1 * isAscending
            : textA.toString().toUpperCase() > textB.toString().toUpperCase()
              ? 1 * isAscending
              : 0;
        } else if (typeof textA === 'number' && typeof textB === 'number') {
          return textA < textB ? -1 * isAscending : textA > textB ? 1 * isAscending : 0;
        }
      });
    } else if (this.showMFARMMega) {
      this.MFARMMegaList.sort((a: any, b: any) => {
        const textA = a[columnName];
        const textB = b[columnName];
        if (typeof textA === 'string' && typeof textB === 'string') {
          return textA.toString().toUpperCase() < textB.toString().toUpperCase()
            ? -1 * isAscending
            : textA.toString().toUpperCase() > textB.toString().toUpperCase()
              ? 1 * isAscending
              : 0;
        } else if (typeof textA === 'number' && typeof textB === 'number') {
          return textA < textB ? -1 * isAscending : textA > textB ? 1 * isAscending : 0;
        }
      });
    }

    this.presentSortedColumn = columnName;
  }

  public goToEnterMega(committedMega): void {
    this.editMegaClickedCount = this.editMegaClickedCount + 1;
    this.editMegaClick.emit({
      poolNumber: committedMega.poolNumber,
      editMegaClickedCount: this.editMegaClickedCount,
    });
  }

  public downloadCollateralPool(committedMega): void {
    this.downloadCollateralClicked.emit({ poolNumber: committedMega.poolNumber });
  }

  public onFileUpload($event, committedMega) {
    const uploadFile = $event[0];
    this.fileUploaded.emit({ committedMega, file: uploadFile });
    committedMega.status = 'VALIDATING';
  }

  public routeToPoolInfo(): void {
    if (this.router) {
      this.router.navigate(['/mega-upload/upload-pool-info']);
    }
  }

  private onInitTooltip(): void {}

  ngAfterViewChecked(): void {
    if (!this.tooltipsInitialized) {
      this.initializeTooltips();
    }
  }

  private initializeTooltips(): void {
    const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
    if (tooltipTriggerList.length > 0) {
      tooltipTriggerList.forEach((tooltipTriggerEl) => {
        if (!Tooltip.getInstance(tooltipTriggerEl)) {
          new Tooltip(tooltipTriggerEl, {
            customClass: 'fm-tooltip',
          });
        }
      });
      this.tooltipsInitialized = true;
    }
  }

  public needsDisclaimer(mega: any): boolean {
    if (this.feeDisclaimerService.needsDisclaimer(mega.prefixCode) && mega.isAllocated === 'N') {
      this.disclaimerMessage = StringConstants.SUPERS_PREFIX_FEE_DISCLAIMER;
      return true;
    }
    return false;
  }

  public settlementDateCheck(settlementDate: string): boolean {
    const nowInEST = moment().tz('America/New_York');
    const settlementCutOffInEST = moment
      .tz(settlementDate, 'America/New_York')
      .subtract(1, 'day')
      .hour(15)
      .minute(0)
      .second(0);
    return nowInEST.isBefore(settlementCutOffInEST);
  }

  public openDeallocateModal(currentMega: object): void {
    this.showDeallocateModal = true;
    this.currentTrade = Object.assign({}, currentMega);
  }

  public closeModal(): void {
    this.showDeallocateModal = false;
  }

  public closeErrorModal() {
    this.deallocationError = false;
  }

  public deallocateCollateral(): void {
    this.showDeallocateModal = false;
    this.processingDeallocation = true;
    this.cdr.markForCheck();

    this.megaService
      .deallocateCollateral({ trustId: this.currentTrade.poolNumber })
      .pipe(finalize(() => this.clearDeallocationSpinner()))
      .subscribe({
        next: (data) => this.handleDeallocateSuccess(data),
        error: (error) => this.handleDeallocateFail(error),
      });
  }

  public handleDeallocationFeeCommitSuccess(_response: any) {
    this.deallocation.emit();
  }

  public handleDeallocationFeeCommitFail(error: any) {
    this.errorHandlerService.setErrorObj(error.error);
    this.router.navigate(['error']);
  }

  public handleDeallocateSuccess(_response: any) {
    if (this.currentTrade.hasFreddieCollateral) {
      const deallocateFeeRequest = {
        poolNumber: this.currentTrade.poolNumber,
        freddieIssuedAmt: 0,
        hasFreddieCollateral: false,
        packageRevisionNumber: this.currentTrade.packageRevisionNumber,
        tradeRevisionNumber: this.currentTrade.tradeRevisionNumber,
      };

      this.megaService.deallocateFreddieCollateral(deallocateFeeRequest).subscribe({
        next: (data) => this.handleDeallocationFeeCommitSuccess(data),
        error: (error) => this.handleDeallocationFeeCommitFail(error),
      });
    } else {
      this.deallocation.emit();
    }
  }

  public handleDeallocateFail(error: any) {
    this.errorHandlerService.setErrorObj(error.error);

    if (error && error.status === 400) {
      this.deallocationError = true;
      this.cdr.markForCheck();
    } else {
      this.router.navigate(['error']);
    }
  }

  private clearDeallocationSpinner(): void {
    this.processingDeallocation = false;
    this.cdr.markForCheck();
  }

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }
}
